import React, { useState } from 'react';
import { Plus, Server, Cable, Package, Circle } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useProjectStore } from '../../store/projectStore';
import NodeContextMenu from '../Canvas/NodeContextMenu';
import { formatCurrencyNoDecimals } from '../../utils/currencyUtils';
import { uniqueCablesById } from '../../utils/cableRuns';

const LeftSidebar: React.FC<{ onCreateDesign: () => void }> = ({ onCreateDesign }) => {
  const [nodeContextMenu, setNodeContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({ isOpen: false, position: { x: 0, y: 0 } });

  const {
    setAddEquipmentOpen,
    setRackBuilderOpen,
    isCableRunMode,
    setCableRunMode,
    setSelectedRack,
    currency,
    showCanvasToolbar,
    showDesignInfoOverlay,
    getCurrentDesign,
    currentProject,
  } = useProjectStore(
    useShallow((s) => ({
      setAddEquipmentOpen: s.setAddEquipmentOpen,
      setRackBuilderOpen: s.setRackBuilderOpen,
      isCableRunMode: s.isCableRunMode,
      setCableRunMode: s.setCableRunMode,
      setSelectedRack: s.setSelectedRack,
      currency: s.currency,
      showCanvasToolbar: s.showCanvasToolbar,
      showDesignInfoOverlay: s.showDesignInfoOverlay,
      getCurrentDesign: s.getCurrentDesign,
      currentProject: s.currentProject,
    }))
  );
  void currentProject;

  const currentDesign = getCurrentDesign();
  const designCables = uniqueCablesById(currentDesign?.cables);

  // Hide the entire left sidebar when the canvas toolbar is enabled
  if (showCanvasToolbar) {
    return null;
  }

  return (
    <div className="w-80 bg-slate-900 text-white overflow-hidden relative">
      <div className="w-80 flex-shrink-0 overflow-y-auto custom-scrollbar p-4" style={{ height: 'calc(100vh - 120px)' }}>
        <div className="space-y-2">
          {!showCanvasToolbar && (
            <>
              <div>
                <button
                  onClick={() => {
                    setSelectedRack(null);
                    setRackBuilderOpen(true);
                  }}
                  className="w-full bg-slate-800 hover:bg-slate-700 rounded-lg p-2 flex items-center space-x-2 transition-colors text-sm"
                >
                  <Server className="text-blue-400" size={20} />
                  <span className="font-medium">Rack Builder</span>
                  <Plus size={16} className="ml-auto" />
                </button>
              </div>

              <div>
                <button
                  onClick={() => setAddEquipmentOpen(true)}
                  className="w-full bg-slate-800 hover:bg-slate-700 rounded-lg p-2 flex items-center space-x-2 transition-colors text-sm"
                >
                  <Package className="text-green-400" size={20} />
                  <span className="font-medium">Add Equipment</span>
                  <Plus size={16} className="ml-auto" />
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setCableRunMode(!isCableRunMode)}
                  className={`flex-1 rounded-lg p-2 flex items-center space-x-2 transition-colors text-sm ${
                    isCableRunMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-slate-800 hover:bg-slate-700'
                  }`}
                >
                  <Cable className="text-amber-400" size={18} />
                  <span className="font-medium">Cable Run</span>
                </button>
                <button
                  onClick={(e) => {
                    setNodeContextMenu({
                      isOpen: true,
                      position: { x: e.clientX, y: e.clientY },
                    });
                  }}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 rounded-lg p-2 flex items-center space-x-2 transition-colors text-sm"
                >
                  <Circle className="text-purple-400" size={18} />
                  <span className="font-medium">Node</span>
                </button>
              </div>

              <p className="text-[11px] text-slate-500 px-1 pt-1">
                Object list moved to the canvas Browser panel (left).
              </p>
            </>
          )}

          {showDesignInfoOverlay && currentDesign && (
            <div className="bg-slate-800 rounded-lg p-3 space-y-1.5 text-xs mt-4">
              <div className="font-semibold text-gray-300 uppercase tracking-wider text-[11px] mb-2">
                Design Info
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Equipment:</span>
                <span>
                  {currentDesign.equipment.filter((eq) => !eq.isExisting).length || 0} new,{' '}
                  {currentDesign.equipment.filter((eq) => eq.isExisting).length || 0} existing
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Cables:</span>
                <span>{designCables.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Racks:</span>
                <span>{currentDesign.racks.length}</span>
              </div>
              <div className="flex justify-between border-t border-slate-700 pt-1.5 mt-1.5">
                <span className="text-gray-400">Est. cost:</span>
                <span>
                  {formatCurrencyNoDecimals(
                    currentDesign.equipment
                      .filter((eq) => !eq.isExisting)
                      .reduce((total, eq) => total + (eq.equipment.price || 0), 0) || 0,
                    currency
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <NodeContextMenu
        isOpen={nodeContextMenu.isOpen}
        position={nodeContextMenu.position}
        onClose={() => setNodeContextMenu({ isOpen: false, position: { x: 0, y: 0 } })}
      />
    </div>
  );
};

export default LeftSidebar;
